import { useLogin } from '@/hooks/use-login';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { WithAuthenticationRequiredOptions } from '@rhg/libs/auth0';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { Box, Toolbar } from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import React, { ComponentType, FC, useEffect } from 'react';

/*
 * Slightly modified version of the HoC definition from @auth0/auth0-react
 * Modified the default for returnTo and onRedirecting variable definitions
 * Modified to useAuth() instead of useAuth0() to support e2e
 *
 * https://github.com/auth0/auth0-react/blob/1f460701ab9cef676a4748ca3cd2f9f38411f2d1/src/with-authentication-required.tsx
 */
const withAuthenticationRequired = <P extends object>(
    Component: ComponentType<P>,
    options: WithAuthenticationRequiredOptions = {},
    LoadingComponent?: FC
): FC<P> => {
    return function WithAuthenticationRequired(props: P): React.JSX.Element {
        /**
         * The route is authenticated if the user has valid auth and there are no
         * JWT claim mismatches.
         */
        const { isAuthenticated, isLoading, error } = useAuth();
        const { loginIfNotRun, isReady: isLoginReady } = useLogin();
        const { push, isReady: isRouterReady } = useRouter();

        useEffect(() => {
            if (
                isLoading ||
                !isLoginReady ||
                !isRouterReady ||
                isAuthenticated
            ) {
                return;
            }
            if (error) {
                push(
                    routes.error({ title: error.name, body: error.message })
                ).catch((err) =>
                    logger.error(
                        'Failed to navigate to error page',
                        {},
                        err as Error
                    )
                );
                return;
            }

            const defaultReturnTo = () =>
                `${window.location.pathname}${window.location.search}`;

            const { returnTo = defaultReturnTo, loginOptions } = options;

            const opts = {
                ...loginOptions,
                appState: {
                    ...(loginOptions && loginOptions.appState),
                    returnTo:
                        typeof returnTo === 'function' ? returnTo() : returnTo,
                },
            };

            loginIfNotRun(opts).catch((err) =>
                logger.error('Failed to run login', {}, err as Error)
            );
        }, [
            error,
            isAuthenticated,
            isLoading,
            isLoginReady,
            isRouterReady,
            loginIfNotRun,
            push,
        ]);

        return isAuthenticated ? (
            <Component {...props} />
        ) : (
            <Box sx={{ position: 'fixed', left: 0, top: 0 }}>
                {LoadingComponent ? (
                    <LoadingComponent />
                ) : (
                    <>
                        <Toolbar />
                        <FullWidthLinearProgress />
                    </>
                )}
            </Box>
        );
    };
};

/**
 * This is a wrapper on top of withAuthenticationRequired
 * that tells Auth0 to use the wrapped page as the
 * loading state, preserving SSR for the wrapped page.
 */
const withAuth = (
    WrappedComponent: FC,
    LoadingComponent?: FC,
    options: WithAuthenticationRequiredOptions = {}
) => {
    const AuthenticatedState = () => <WrappedComponent />;
    return withAuthenticationRequired(
        AuthenticatedState,
        options,
        LoadingComponent
    );
};

export default withAuth;
